import React, { useCallback, useEffect, useState } from 'react';
import { listGuides, type Guide } from '../../data/guidesService';
import { getSupabaseClient } from '../../lib/supabase';
import GuideStatusBadge from '../../components/GuideStatusBadge';

async function hydrateGuideStats(items: Guide[]): Promise<Guide[]> {
  const guideIds = items.map((g) => g.id).filter((id): id is string => !!id);
  if (guideIds.length === 0) return items;

  const supabase = getSupabaseClient();

  // Fetch all active matches for connected seekers count
  const { data: matchesData, error: matchesError } = await supabase
    .from('matches')
    .select('guide_id, seeker_id')
    .eq('status', 'active');

  if (matchesError) {
    console.warn('Failed to load matches:', matchesError);
  }

  // Build seeker map from active matches
  const seekerMap = new Map<string, Set<string>>();
  (matchesData ?? []).forEach((match) => {
    if (!match.guide_id || !match.seeker_id) return;
    if (!seekerMap.has(match.guide_id)) {
      seekerMap.set(match.guide_id, new Set());
    }
    seekerMap.get(match.guide_id)!.add(match.seeker_id);
  });

  return items.map((guide) => {
    const seekers = seekerMap.get(guide.id) ?? new Set<string>();
    return {
      ...guide,
      connectedSeekers: seekers.size,
    };
  });
}

type ManualMatchModalProps = {
  seekerId: string;
  seekerName: string;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ManualMatchModal({
  seekerId,
  seekerName,
  onClose,
  onSuccess,
}: ManualMatchModalProps) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listGuides({
      status: 'active', // Only show approved guides
      pageSize: 100, // Get more guides for selection
    })
      .then((result) => hydrateGuideStats(result.items))
      .then((hydratedItems) => setGuides(hydratedItems))
      .catch((err) => {
        console.error('Failed to load guides:', err);
        setError('Failed to load guides');
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredGuides = guides.filter((guide) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      guide.name.toLowerCase().includes(query) ||
      guide.email.toLowerCase().includes(query) ||
      (guide.region && guide.region.toLowerCase().includes(query))
    );
  });

  const selectedGuide = guides.find((g) => g.id === selectedGuideId);

  const handleMatch = useCallback(async () => {
    if (!selectedGuideId) return;

    setMatching(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error: functionError } = await supabase.functions.invoke('manual-match', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          seeker_id: seekerId,
          guide_id: selectedGuideId,
        },
      });

      if (functionError) {
        throw functionError;
      }

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to create manual match');
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create manual match:', err);
      setError(err instanceof Error ? err.message : 'Failed to create manual match');
    } finally {
      setMatching(false);
    }
  }, [selectedGuideId, seekerId, onSuccess, onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2C2731]/70 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
          <div>
            <h2 className="text-xl font-medium text-[#2C2731] font-recoleta">
              Manual Match for <span className="ph-mask">{seekerName}</span>
            </h2>
            <p className="text-sm text-[#2C2731]/70 mt-1">
              Select a guide to manually create a match
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search guides by name, email, or region..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-[#2C2731] bg-white px-4 py-2.5 text-sm text-[#2C2731] placeholder-[#2C2731]/50 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
            />
          </div>

          {/* Guides List */}
          <div className="max-h-[280px] overflow-y-auto rounded-lg border border-[#2C2731]/20 bg-white">
            {loading && (
              <div className="py-8 text-center text-sm text-[#2C2731]/70">
                Loading guides...
              </div>
            )}

            {!loading && filteredGuides.length === 0 && (
              <div className="py-8 text-center text-sm text-[#2C2731]/70">
                {searchQuery ? 'No guides found matching your search' : 'No approved guides available'}
              </div>
            )}

            {!loading && filteredGuides.length > 0 && (
              <div className="divide-y divide-[#2C2731]/10">
                {filteredGuides.map((guide) => (
                  <div
                    key={guide.id}
                    onClick={() => setSelectedGuideId(guide.id)}
                    className={`cursor-pointer px-3 py-2 transition-colors hover:bg-[#F2EAC3] ${
                      selectedGuideId === guide.id ? 'bg-[#F2EAC3]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="radio"
                          checked={selectedGuideId === guide.id}
                          onChange={() => setSelectedGuideId(guide.id)}
                          className="h-4 w-4 flex-shrink-0 border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#2C2731] truncate ph-mask">{guide.name}</span>
                            <GuideStatusBadge status={guide.status} />
                          </div>
                          <div className="text-xs text-[#2C2731]/70 truncate ph-mask">
                            {guide.email}
                            {guide.region && ` · ${guide.region}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-[#2C2731]/70 flex-shrink-0">
                        {guide.connectedSeekers} seekers
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Guide Info */}
          {selectedGuide && (
            <div className="rounded-lg border border-[#2C2731]/20 bg-[#F2EAC3] p-4">
              <div className="text-sm font-medium text-[#2C2731] mb-2">Selected Guide</div>
              <div className="text-sm text-[#2C2731]">
                <strong className="ph-mask">{selectedGuide.name}</strong> ({selectedGuide.email}) will be matched with{' '}
                <strong className="ph-mask">{seekerName}</strong>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg border border-red-500 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#2C2731]/20 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={matching}
            className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleMatch}
            disabled={!selectedGuideId || matching}
            className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {matching ? 'Creating Match...' : 'Create Match'}
          </button>
        </div>
      </div>
    </div>
  );
}
